import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Ticket,
  BookOpen,
  Star,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/blog", label: "Journal", icon: BookOpen },
  { to: "/admin/testimonials", label: "Reviews", icon: Star },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export const AdminLayout = () => {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) return <div className="min-h-screen bg-bone flex items-center justify-center text-bark">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-layout">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 h-14 bg-espresso text-bone flex items-center justify-between px-4 z-40 border-b border-white/10">
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="admin-menu-toggle"
          aria-label="Open menu"
          className="p-2 -ml-2"
        >
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-serif text-lg">17/11 Admin</span>
        <button
          onClick={() => { logout(); navigate("/admin/login"); }}
          data-testid="admin-logout-mobile"
          aria-label="Logout"
          className="p-2 -mr-2 text-bone/80"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-espresso/50 z-40"
          onClick={closeMobile}
          data-testid="admin-drawer-overlay"
        />
      )}

      {/* Sidebar / Drawer */}
      <aside
        className={`bg-espresso text-bone flex flex-col fixed h-full z-50 transition-transform duration-200
          md:w-60 md:translate-x-0
          w-72 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <div className="h-14 md:h-16 flex items-center justify-between px-5 md:px-6 border-b border-white/10">
          <span className="font-serif text-xl">17/11 Admin</span>
          <button
            onClick={closeMobile}
            aria-label="Close menu"
            className="md:hidden p-2 -mr-2 text-bone/80"
            data-testid="admin-drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 py-4 space-y-1 px-3">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={closeMobile}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-terracotta text-bone" : "text-bone/70 hover:bg-white/10"
                }`
              }
            >
              <n.icon className="w-5 h-5 shrink-0" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <button
          onClick={() => {
            logout();
            navigate("/admin/login");
          }}
          data-testid="admin-logout"
          className="flex items-center gap-3 px-5 py-4 border-t border-white/10 text-bone/70 hover:text-bone text-sm"
        >
          <LogOut className="w-5 h-5" /> <span>Logout</span>
        </button>
      </aside>

      <main className="md:ml-60 pt-14 md:pt-0 p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
};
